import React, { FC, useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Box, Typography, Button } from '@mui/material'
import { Add } from '@mui/icons-material'
import axios from 'axios'
import { BaseLayout, Loader, CustomAlert } from 'components'
import FactorCard from 'components/factor-card'
import { api, routes } from 'config'
import { MfaEnrollment, FACTOR_TYPES } from 'models/Mfa.d'
import { processErrorMsg } from 'utils'
import useStyles from './manage-mfa-styles'

interface LocationState {
  success?: string
}

const ManageMfa: FC = () => {
  const { classes } = useStyles()
  const navigate = useNavigate()
  const location = useLocation()
  const locationState = location.state as LocationState | null
  const [enrollment, setEnrollment] = useState<MfaEnrollment | null>(null)
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const [showLoader, setShowLoader] = useState(false)
  const [serviceError, setServiceError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  const accessToken = JSON.parse(
    localStorage.getItem('okta-token-storage') ?? '{}',
  )?.accessToken?.accessToken

  useEffect(() => {
    if (locationState?.success) {
      setSuccessMessage(locationState.success)
      setTimeout(() => setSuccessMessage(''), 3000)
      globalThis.history.replaceState({}, document.title)
    }
    loadFactors(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadFactors = async (isInitial = false) => {
    if (!isInitial) {
      setShowLoader(true)
    }
    setServiceError('')

    try {
      const response = await axios.get(api.getMfaFactors(), {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })

      setEnrollment(response.data)
      setShowLoader(false)
      setIsInitialLoading(false)
    } catch (error) {
      setShowLoader(false)
      setIsInitialLoading(false)
      setServiceError(
        processErrorMsg(error, 'Manage MFA', 'Failed to load MFA factors'),
      )
    }
  }

  const handleRemoveFactor = async (factorId: string) => {
    if (
      !window.confirm(
        'Are you sure you want to remove this authentication method?',
      )
    ) {
      return
    }

    setShowLoader(true)
    setServiceError('')

    try {
      await axios.delete(api.removeMfaFactor(factorId), {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })

      setSuccessMessage('Authentication method removed successfully')
      setTimeout(() => setSuccessMessage(''), 3000)
      loadFactors()
    } catch (error) {
      setShowLoader(false)
      setServiceError(
        processErrorMsg(
          error,
          'Manage MFA',
          'Failed to remove authentication method',
        ),
      )
    }
  }

  const handleAddFactor = () => {
    navigate(routes.addMfaFactor)
  }

  const handleActivateFactor = (factorId: string) => {
    const pendingFactor = enrollment?.factors.find((f) => f.id === factorId)
    if (pendingFactor) {
      navigate(routes.addMfaFactor, {
        state: {
          pendingFactorId: factorId,
          pendingFactorType: pendingFactor.type,
        },
      })
    }
  }

  const getAvailableFactors = () => {
    if (!enrollment) return []

    const enrolledTypes = enrollment.factors.map((f) => f.type)
    return Object.values(FACTOR_TYPES).filter(
      (factorMeta) => !enrolledTypes.includes(factorMeta.type),
    )
  }

  const availableFactors = getAvailableFactors()

  return (
    <BaseLayout>
      <Loader visible={showLoader} />
      {isInitialLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="300px"
        >
          <Loader visible={true} />
        </Box>
      ) : (
        <Box className={classes.pageContainer}>
          <Box className={classes.contentWrapper}>
            <Typography variant="h1" className={classes.title}>
              Manage Two-Factor Authentication
            </Typography>

            <Typography variant="body1" className={classes.subtitle}>
              Manage your authentication methods for added security.
            </Typography>

            {successMessage && (
              <CustomAlert severity="success">{successMessage}</CustomAlert>
            )}

            {serviceError && (
              <CustomAlert severity="error">{serviceError}</CustomAlert>
            )}

            <Box className={classes.section}>
              <Typography variant="h6" className={classes.sectionTitle}>
                Active Authentication Methods
              </Typography>

              {enrollment?.factors && enrollment.factors.length > 0 ? (
                <Box className={classes.factorsContainer}>
                  {enrollment.factors.map((factor) => (
                    <FactorCard
                      key={factor.id}
                      factor={factor}
                      onRemove={handleRemoveFactor}
                      onActivate={handleActivateFactor}
                      showActions={true}
                      showFullDetails={true}
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No authentication methods found. Please contact support.
                </Typography>
              )}
            </Box>

            {availableFactors.length > 0 && (
              <Box className={classes.section}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Add Additional Method
                </Typography>

                <Typography variant="body2" className={classes.description}>
                  Enhance your account security by adding more authentication
                  methods:
                </Typography>

                <Box className={classes.availableFactorsContainer}>
                  {availableFactors.map((factorMeta) => (
                    <Box
                      key={factorMeta.type}
                      className={classes.availableFactorCard}
                    >
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {factorMeta.displayName}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {factorMeta.description}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>

                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Add />}
                  onClick={handleAddFactor}
                  className={classes.addButton}
                >
                  Add Authentication Method
                </Button>
              </Box>
            )}

            <Box className={classes.backButtonContainer}>
              <Button
                variant="outlined"
                onClick={() => navigate(routes.passwordSecurity)}
              >
                Back to Password & Security
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </BaseLayout>
  )
}

export default ManageMfa
